import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { useDismissOnboarding } from "@/app/onboarding/api/hooks";
import { useOnboardingTelemetry } from "@/app/onboarding-first-win/hooks/use-onboarding-telemetry";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import type { FileRouteTypes } from "@/routeTree.gen";

type AppRoute = FileRouteTypes["to"];

type LeaveOnboardingArgs = {
  to: AppRoute;
  event?: string;
  properties?: Record<string, unknown>;
};

export function useLeaveOnboarding() {
  const navigate = useNavigate();
  const dismiss = useDismissOnboarding();
  const { captureOnboardingEvent } = useOnboardingTelemetry();
  const { organizationId, projectId } = useOrgProjectRouteContext();

  return useCallback(
    ({
      to,
      event = "Onboarding dismissed",
      properties,
    }: LeaveOnboardingArgs) => {
      dismiss.mutate(undefined, {
        onSuccess: () => {
          captureOnboardingEvent(event, properties);
        },
        onSettled: () => {
          navigate({
            to,
            params: { organizationId, projectId },
          });
        },
      });
    },
    [
      captureOnboardingEvent,
      dismiss.mutate,
      navigate,
      organizationId,
      projectId,
    ]
  );
}
